<script>
    import { fade } from 'svelte/transition';
    import { push } from 'svelte-spa-router';
    import { onMount } from 'svelte';
    import Header from '../components/Header.svelte';
    import TopInfo from '../components/TopInfo.svelte';
    import Preloader from '../components/Preloader.svelte';
    import UserStore from '../stores/user';
    import fetchData from '../api.js';

    const PURCHASE_ORDERS_LIST_ACTION = 'crm_get_purchase_orders';
 
    let isLoading = false;
    let loadError = '';
    let orders = [];
    let searchTerm = '';
    let dateFrom = '';
    let dateTo = '';

    onMount(() => {
        if (!$UserStore || !$UserStore.data) {
            push('/login');
            return;
        }

        fetchOrders();
    });

    function getItemsFromOrder(order) {
        if (Array.isArray(order.order_items)) {
            return order.order_items;
        }

        if (Array.isArray(order.items)) {
            return order.items;
        }

        if (Array.isArray(order.products)) {
            return order.products;
        }

        return [];
    }

    function normalizeDate(rawDate) {
        if (!rawDate) {
            return { date: '-', time: '-' };
        }

        const parsed = new Date(rawDate);
        if (Number.isNaN(parsed.getTime())) {
            const dateTimeParts = String(rawDate).split(' ');
            return {
                date: dateTimeParts[0] || '-',
                time: dateTimeParts[1] || '-'
            };
        }

        const yyyy = parsed.getFullYear();
        const mm = String(parsed.getMonth() + 1).padStart(2, '0');
        const dd = String(parsed.getDate()).padStart(2, '0');
        const hh = String(parsed.getHours()).padStart(2, '0');
        const min = String(parsed.getMinutes()).padStart(2, '0');
        const sec = String(parsed.getSeconds()).padStart(2, '0');

        return {
            date: `${yyyy}-${mm}-${dd}`,
            time: `${hh}:${min}:${sec}`
        };
    }

    function normalizeOrder(rawOrder, index) {
        const orderId = rawOrder.id || rawOrder.order_id || rawOrder.purchase_order_id || `row-${index}`;
        const dbId = rawOrder.id || '';
        const purchaseOrderId = rawOrder.purchase_order_id || '';
        const createdRaw = rawOrder.created_at || rawOrder.created || rawOrder.date || rawOrder.datetime || '';
        const dateData = normalizeDate(createdRaw);
        const orderItems = getItemsFromOrder(rawOrder);
        const orderDetails = [
            { label: 'מזהה הזמנה', value: orderId },
            { label: 'מספר הזמנה', value: rawOrder.order_number || rawOrder.reference || rawOrder.external_id || '-' },
            { label: 'סטטוס', value: rawOrder.status || rawOrder.state || '-' },
            { label: 'תאריך יצירה', value: dateData.date },
            { label: 'שעת יצירה', value: dateData.time },
            { label: 'מספר פריטים', value: orderItems.length }
        ];

        return {
            id: orderId,
            dbId,
            purchaseOrderId,
            orderNumber: rawOrder.order_number || rawOrder.reference || rawOrder.external_id || '-',
            status: rawOrder.status || rawOrder.state || '-',
            createdDate: dateData.date,
            createdTime: dateData.time,
            itemCount: orderItems.length,
            details: orderDetails,
            items: orderItems.map((item) => ({
                sku: item.sku || item.product_sku || '',
                barcode: item.barcode || item.ean || '',
                description_product: item.description_product || item.description || item.product_name || '',
                quantity: item.quantity || item.qty || 0
            }))
        };
    }

    async function fetchOrders() {
        loadError = '';
        isLoading = true;

        try {
            const filters = {};
            if (searchTerm.trim()) {
                filters.search = searchTerm.trim();
            }
            if (dateFrom) {
                filters.date_from = dateFrom;
            }
            if (dateTo) {
                filters.date_to = dateTo;
            }

            const postData = {
                action: PURCHASE_ORDERS_LIST_ACTION,
                token: $UserStore.data,
                filters,
                include_items: 1
            };

            const response = await fetchData(postData);

            if (!response) {
                loadError = 'אירעה שגיאה בזמן טעינת הזמנות הרכש';
                orders = [];
                return;
            }

            if (response.error || response.success === 0) {
                loadError = response.msg || response.error || 'טעינת הזמנות הרכש נכשלה';
                orders = [];
                return;
            }

            const rawOrders = Array.isArray(response.data)
                ? response.data
                : Array.isArray(response.orders)
                    ? response.orders
                    : [];

            orders = rawOrders.map((order, index) => normalizeOrder(order, index));
        } catch (error) {
            loadError = error.message || 'אירעה שגיאה בזמן טעינת הזמנות הרכש';
            orders = [];
        } finally {
            isLoading = false;
        }
    }

    function openOrder(order) {
        if (!order.dbId) {
            loadError = 'לא ניתן לפתוח הזמנה ללא מזהה פנימי';
            return;
        }

        push(`/purchase-orders/${order.dbId}`);
    }
</script>

<div class="page-container" transition:fade={{ delay: 250, duration: 300 }}>
    <Preloader {isLoading} />
    <Header />

    <div class="page">
        <TopInfo />

        <div class="shipment purchase-orders-history-page">
            <div class="header-row">
                <h1 class="text-[22px] font-bold">היסטוריית הזמנות רכש</h1>
                <div class="nav-actions">
                    <button type="button" class="create_button" on:click={() => push('/purchase-orders/items')}>פריטי רכש</button>
                    <button type="button" class="create_button" on:click={() => push('/purchase-orders')}>הזמנת רכש חדשה</button>
                </div>
            </div>

            <p class="subtitle">צפיה בכל ההזמנות שנוצרו, כולל תאריך, שעה, סטטוס ורשימת פריטים בכל הזמנה.</p>

            <div class="card filters">
                <div class="filter-grid">
                    <div class="filter-item">
                        <label for="po-history-search">חיפוש</label>
                        <input id="po-history-search" type="text" bind:value={searchTerm} placeholder="מספר הזמנה / רפרנס / SKU" />
                    </div>

                    <div class="filter-item">
                        <label for="po-history-date-from">מתאריך</label>
                        <input id="po-history-date-from" type="date" bind:value={dateFrom} />
                    </div>

                    <div class="filter-item">
                        <label for="po-history-date-to">עד תאריך</label>
                        <input id="po-history-date-to" type="date" bind:value={dateTo} />
                    </div>
                </div>

                <div class="actions">
                    <button type="button" class="create_button" on:click={fetchOrders}>רענן רשימה</button>
                </div>
            </div>

            {#if loadError}
                <div class="error">{loadError}</div>
            {/if}

            <div class="card">
                {#if !orders.length && !isLoading}
                    <div class="empty">לא נמצאו הזמנות רכש להצגה.</div>
                {:else}
                    <div class="table-wrap">
                        <table class="orders-table">
                            <thead>
                                <tr>
                                    <th>פרטי הזמנה</th>
                                    <th>תאריך</th>
                                    <th>שעה</th>
                                    <th>פריטים</th>
                                    <th>סטטוס</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {#each orders as order, orderIndex (order.id + '-' + orderIndex)}
                                    <tr>
                                        <td>
                                            <div class="order-meta">
                                                <div class="order-id">#{order.id}</div>
                                                <div class="order-number">{order.orderNumber}</div>
                                            </div>
                                        </td>
                                        <td>{order.createdDate}</td>
                                        <td>{order.createdTime}</td>
                                        <td>{order.itemCount}</td>
                                        <td>{order.status}</td>
                                        <td>
                                            <button
                                                type="button"
                                                class="toggle-button"
                                                on:click={() => openOrder(order)}
                                            >
                                                הצג הזמנה
                                            </button>
                                        </td>
                                    </tr>
                                {/each}
                            </tbody>
                        </table>
                    </div>
                {/if}
            </div>

        </div>
    </div>
</div>

<style>
    .purchase-orders-history-page {
        width: 100%;
        padding: 20px;
        box-sizing: border-box;
    }

    .header-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        flex-wrap: wrap;
    }

    .nav-actions {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }

    .subtitle {
        margin-top: 8px;
        color: #5b6475;
    }

    .card {
        background: #fff;
        border-radius: 14px;
        border: 1px solid #eceff5;
        box-shadow: 0 8px 24px rgba(17, 43, 85, 0.08);
        padding: 16px;
        margin-top: 16px;
    }

    .filter-grid {
        display: grid;
        grid-template-columns: 2fr 1fr 1fr;
        gap: 12px;
    }

    .filter-item label {
        display: block;
        font-size: 13px;
        color: #4f5970;
        margin-bottom: 6px;
    }

    .filter-item input {
        width: 100%;
        min-height: 38px;
        border: 1px solid #d6deea;
        border-radius: 8px;
        padding: 0 10px;
        box-sizing: border-box;
    }

    .actions {
        margin-top: 12px;
        display: flex;
        justify-content: flex-end;
    }

    .table-wrap {
        width: 100%;
        overflow: auto;
    }

    .orders-table {
        width: 100%;
        border-collapse: collapse;
    }

    .orders-table {
        min-width: 900px;
    }

    .orders-table th,
    .orders-table td {
        padding: 10px 8px;
        border-bottom: 1px solid #eef2f7;
        vertical-align: middle;
    }

    .orders-table th {
        text-align: right;
        color: #3f4760;
        font-size: 13px;
    }

    .order-meta {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .order-id {
        font-weight: 700;
        color: #23314f;
    }

    .order-number {
        color: #667089;
        font-size: 13px;
    }

    .toggle-button {
        border: 1px solid #cfd9ea;
        background: #f8fbff;
        color: #2f487a;
        min-height: 34px;
        border-radius: 8px;
        padding: 0 10px;
        cursor: pointer;
        white-space: nowrap;
    }

    .empty {
        text-align: center;
        color: #6f7a92;
        padding: 14px;
    }

    .error {
        margin-top: 12px;
        border-radius: 10px;
        padding: 10px 12px;
        font-size: 14px;
        background: #fff2f4;
        color: #b2203f;
        border: 1px solid #ffd6de;
    }

    @media screen and (max-width: 900px) {
        .purchase-orders-history-page {
            padding: 10px;
        }

        .filter-grid {
            grid-template-columns: 1fr;
        }

    }
</style>
